import { useMemo, useCallback, useRef } from 'react';
import GridLayout from 'react-grid-layout';
import type { LayoutItem, WidgetConfig, ExecutionDataRow, MetricDefinition } from '@monitoring-panel/types';
import { Widget } from './Widget';
import 'react-grid-layout/css/styles.css';

interface DashboardGridProps {
  layout: LayoutItem[];
  widgets: WidgetConfig[];
  campaignData: ExecutionDataRow[];
  availableColumns: string[];
  availableMetrics: MetricDefinition[];
  onLayoutChange: (layout: LayoutItem[]) => void;
  onUpdateWidget: (widgetId: string, updates: Partial<WidgetConfig>) => void;
  onDeleteWidget: (widgetId: string) => void;
}

export function DashboardGrid({
  layout,
  widgets,
  campaignData,
  availableColumns,
  availableMetrics,
  onLayoutChange,
  onUpdateWidget,
  onDeleteWidget,
}: DashboardGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce layout changes to avoid excessive saves
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const handleLayoutChange = useCallback(
    (newLayout: GridLayout.Layout[]) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const converted: LayoutItem[] = newLayout.map((item) => ({
          i: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          minW: item.minW,
          minH: item.minH,
          maxW: item.maxW,
          maxH: item.maxH,
        }));
        onLayoutChange(converted);
      }, 500);
    },
    [onLayoutChange]
  );

  const gridLayout = useMemo(
    () =>
      layout.map((item) => ({
        ...item,
        minW: item.minW ?? 3,
        minH: item.minH ?? 2,
      })),
    [layout]
  );

  if (widgets.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className="dashboard-grid">
      <GridLayout
        className="layout"
        layout={gridLayout}
        cols={12}
        rowHeight={100}
        width={1200}
        isDraggable
        isResizable
        compactType="vertical"
        preventCollision={false}
        onLayoutChange={handleLayoutChange}
        draggableHandle=".widget-drag-handle"
      >
        {widgets.map((widget) => (
          <div key={widget.id}>
            <Widget
              config={widget}
              campaignData={campaignData}
              availableColumns={availableColumns}
              availableMetrics={availableMetrics}
              onUpdate={(updates) => onUpdateWidget(widget.id, updates)}
              onDelete={() => onDeleteWidget(widget.id)}
            />
          </div>
        ))}
      </GridLayout>

      <style>{`
        .dashboard-grid .react-grid-item {
          transition: all 200ms ease;
          transition-property: left, top;
        }
        .dashboard-grid .react-grid-item.cssTransforms {
          transition-property: transform;
        }
        .dashboard-grid .react-grid-item.resizing {
          z-index: 1;
          will-change: width, height;
        }
        .dashboard-grid .react-grid-item.react-draggable-dragging {
          transition: none;
          z-index: 3;
          will-change: transform;
        }
        .dashboard-grid .react-grid-item > .react-resizable-handle {
          position: absolute;
          width: 20px;
          height: 20px;
        }
        .dashboard-grid .react-grid-item > .react-resizable-handle::after {
          content: "";
          position: absolute;
          right: 3px;
          bottom: 3px;
          width: 5px;
          height: 5px;
          border-right: 2px solid var(--border-strong);
          border-bottom: 2px solid var(--border-strong);
        }
        .dashboard-grid .react-resizable-handle-se {
          bottom: 0;
          right: 0;
          cursor: se-resize;
        }
        .dashboard-grid .react-grid-placeholder {
          background: var(--accent-gold);
          opacity: 0.2;
          transition-duration: 100ms;
          z-index: 2;
          border-radius: var(--radius-card);
        }
      `}</style>
    </div>
  );
}
